import { Link } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import Container from "../Container";
import { Product } from "../../types";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Star, Crown, Sparkles, Eye } from "lucide-react";

interface ExclusiveProductsSectionProps {
  products: Product[];
}

const ExclusiveProductsSection = ({
  products,
}: ExclusiveProductsSectionProps) => {
  if (!products || products.length === 0) {
    return null;
  }

  const productUrl = (product: Product) => {
    return product.type === "FeaturedProduct"
      ? `/productos-exclusivos/${product.slug}`
      : `/${product.slug}`;
  };

  return (
    <section className="py-16 bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full blur-3xl exclusive-float"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-3xl exclusive-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full blur-2xl exclusive-float" style={{animationDelay: '4s'}}></div>
      </div>

      <Container>
        <div className="text-center mb-16 relative z-10">
          {/* Premium header with sparkles */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="h-8 w-8 text-yellow-500 animate-pulse" />
            <Crown className="h-10 w-10 text-amber-500" />
            <Sparkles className="h-8 w-8 text-yellow-500 animate-pulse" />
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 dark:from-white dark:via-blue-200 dark:to-purple-200 bg-clip-text text-transparent mb-6">
            Productos Exclusivos
          </h2>
          
          <div className="max-w-3xl mx-auto">
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-4 font-medium">
              Descubre nuestra selección premium de productos únicos
            </p>
            <p className="text-base text-gray-500 dark:text-gray-400">
              Artículos limitados y especiales que no encontrarás en ningún otro lugar
            </p>
          </div>

          {/* Decorative line */}
          <div className="flex items-center justify-center mt-8">
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent w-32"></div>
            <Crown className="h-6 w-6 text-amber-500 mx-4" />
            <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent w-32"></div>
          </div>
        </div>

        <div className="relative">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {products.map((product, index) => (
                <CarouselItem key={product.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full p-5">
                    <div className="group relative bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 h-full animate-fadeInUp exclusive-shimmer" style={{animationDelay: `${index * 150}ms`}}>
                      {/* Premium exclusivity badge */}
                      <div className="absolute top-4 left-4 z-20">
                        <Badge className="gold-gradient text-white font-bold px-3 py-1.5 text-xs shadow-lg border-0 exclusive-glow">
                          <Crown className="h-3 w-3 mr-1" />
                          EXCLUSIVO
                        </Badge>
                      </div>

                      {/* Luxury overlay effect */}
                      <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
                          <Eye className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                        </div>
                      </div>

                      {/* Premium image container */}
                      <Link href={productUrl(product)} className="block relative">
                        <div className="relative w-full h-72 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 overflow-hidden">
                          <img
                            src={
                              product.images?.[0] ||
                              product.image_url ||
                              "https://picsum.photos/seed/exclusive/600/400"
                            }
                            alt={product.name}
                            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                          />
                          
                          {/* Luxury gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          
                          {/* Shimmer effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out"></div>
                        </div>
                      </Link>

                      {/* Premium content section */}
                      <div className="p-6 bg-white dark:bg-gray-800 relative">
                        {/* Brand indicator */}
                        {product.brand_name && (
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500"></div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {product.brand_name}
                            </p>
                          </div>
                        )}

                        {/* Product name with luxury styling */}
                        <h3 className="text-xl font-bold mb-3 line-clamp-2 text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300 leading-tight">
                          <Link href={productUrl(product)} className="hover:underline">
                            {product.name}
                          </Link>
                        </h3>

                        {/* Description with better formatting */}
                        {product.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-3 leading-relaxed">
                            {product.description}
                          </p>
                        )}

                        {/* Premium pricing section */}
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex flex-col">
                            {product.price && (
                              <div className="flex items-center gap-2">
                                {product.sale_price && product.sale_price < Number(product.price) ? (
                                  <>
                                    <span className="text-lg font-bold text-red-600 dark:text-red-400">
                                      S/. {(Number(product.sale_price) || 0).toFixed(2)}
                                    </span>
                                    <span className="text-sm text-gray-500 line-through">
                                      S/. {(Number(product.price) || 0).toFixed(2)}
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-lg font-bold text-gray-900 dark:text-white">
                                    S/. {(Number(product.price) || 0).toFixed(2)}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Premium rating stars */}
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            ))}
                          </div>
                        </div>

                        {/* Luxury CTA button */}
                        <Button 
                          asChild 
                          className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 hover:from-blue-700 hover:via-purple-700 hover:to-blue-800 text-white font-semibold py-3 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-105 border-0"
                        >
                          <Link href={productUrl(product)} className="flex items-center justify-center gap-2">
                            <Eye className="h-4 w-4" />
                            Descubrir Exclusivo
                          </Link>
                        </Button>
                      </div>

                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            
            {/* Premium carousel controls */}
            <CarouselPrevious
              className="left-2 md:left-0 top-1/2 -translate-y-1/2 bg-white/50 dark:bg-gray-800/50 hover:bg-white/80 dark:hover:bg-gray-700/80 transition-all duration-300 hover:scale-110 shadow-lg z-10 w-12 h-12"
              aria-label="Productos exclusivos anteriores"
            />
            <CarouselNext
              className="right-2 md:right-0 top-1/2 -translate-y-1/2 bg-white/50 dark:bg-gray-800/50 hover:bg-white/80 dark:hover:bg-gray-700/80 transition-all duration-300 hover:scale-110 shadow-lg z-10 w-12 h-12"
              aria-label="Productos exclusivos siguientes"
            />
          </Carousel>
        </div>
      </Container>
    </section>
  );
};

export default ExclusiveProductsSection;